import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ShieldOff, Loader2 } from 'lucide-react';
import { api } from '../../../services/api';
import { DeviceAccessTable } from './DeviceAccessTable';
import { buildDeviceAccessRows } from './deviceAccessRows';
import { usePersonDeviceCredentialStates } from '../../../hooks/useDeviceCredentialStates';
import { cn } from '../../../utils/cn';
import { PersonDetailData } from '../index';
import { deviceTypeLabel } from '../../../utils/terminology';
import { holdsPersonCredential } from './credentialCapableDevice';

interface RfidDevice {
  id: string;
  name: string;
  device_type: string;
  is_synced?: boolean;
}

interface RfidZone {
  id: string;
  name: string;
  devices: RfidDevice[];
}

interface RfidDevicesSectionProps {
  person: PersonDetailData;
  darkMode?: boolean;
  readOnly?: boolean;
  /**
   * Remove this person from ONE device. Without it the section stays a pure report — which
   * it was, and which left the all-or-nothing "Revogar" in the header as the only way to
   * clear a single wrong reader, or a credential stranded outside every zone.
   */
  onRevokeDevice?: (deviceId: string, deviceName: string) => void | Promise<void>;
}

export const RfidDevicesSection: React.FC<RfidDevicesSectionProps> = ({
  person,
  darkMode = false,
  readOnly = false,
  onRevokeDevice,
}) => {
  const [revokingDeviceId, setRevokingDeviceId] = useState<string | null>(null);
  const [rfidZones, setRfidZones] = useState<RfidZone[]>([]);
  const [loading, setLoading] = useState(false);


  // Unfiltered: the credential-capable filter is applied below, where the ledger can
  // override it. Filtering here threw away the information needed to second-guess it.
  const fetchRfidZones = useCallback(async () => {
    if (!person.customer?.id || !person.id) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/customers/${person.customer.id}/people/${person.id}/effective-access-zones`
      );
      const zones: any[] = response.data.zones || [];
      setRfidZones(zones.map(zone => ({
        id: zone.id,
        name: zone.name,
        devices: (zone.devices || []) as RfidDevice[],
      })));
    } catch {
      setRfidZones([]);
    } finally {
      setLoading(false);
    }
  }, [person.customer?.id, person.id]);

  useEffect(() => {
    fetchRfidZones();
  }, [fetchRfidZones]);

  // Fetch full device details including credentials for hybrid communication

  // a different (and more trustworthy) question than the app's own is_synced flag.
  const { states: credentialStates } = usePersonDeviceCredentialStates(person.customer?.id, person.id);

  const rfidTableRows = useMemo(() => {
    // No zone column: readers are named after where they are, so it was a column of
    // near-duplicates ("Facial Entrada Instituto" beside the zone "Entrada Instituto").
    // Which zone grants the access is the Zonas de Acesso table's job, above.
    // A device earns a row by being credential-capable OR by the ledger holding a state for
    // this person on it. The allowlist is a guess about hardware; the ledger is a record of
    // what was actually written, and it wins.
    const known = new Set((credentialStates || []).map((s: any) => s.device_id));
    const flatDevices: any[] = rfidZones
      .flatMap(zone => zone.devices)
      .filter((d: any) => holdsPersonCredential(d.device_type) || known.has(d.id));

    return buildDeviceAccessRows(
      flatDevices,
      (credentialStates || []) as any,
      (device: any) => deviceTypeLabel(device.device_type),
    ).map(row => ({
      id: row.deviceId,
      primary: row.deviceName,
      secondary: row.detail,
      stateLabel: row.stateLabel,
      tone: row.tone,
      errorMessage: row.errorMessage,
      orphan: row.orphan,
    }));
  }, [rfidZones, credentialStates]);

  const revokeDevice = useCallback(async (deviceId: string, deviceName: string) => {
    if (!onRevokeDevice) return;
    setRevokingDeviceId(deviceId);
    try {
      await onRevokeDevice(deviceId, deviceName);
    } finally {
      setRevokingDeviceId(null);
    }
  }, [onRevokeDevice]);

  const canRevoke = !!onRevokeDevice && !readOnly;

  const tableRows = useMemo(() => rfidTableRows.map(row => ({
    ...row,
    actions: canRevoke ? (
      <button
        type="button"
        onClick={() => revokeDevice(row.id, row.primary)}
        disabled={revokingDeviceId !== null}
        title={`Remover ${person.first_name} de "${row.primary}"`}
        aria-label={`Revogar ${row.primary}`}
        className={cn(
          'p-1.5 rounded-md transition-colors disabled:opacity-40',
          darkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50',
        )}
      >
        {revokingDeviceId === row.id
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <ShieldOff className="w-3.5 h-3.5" />}
      </button>
    ) : undefined,
  })), [rfidTableRows, canRevoke, revokingDeviceId, revokeDevice, darkMode, person.first_name]);

  const orphanCount = rfidTableRows.filter(row => row.orphan).length;

  if (loading) {
    return (
      <div className={cn(
        'rounded-lg border p-3 text-center text-xs',
        darkMode ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
      )}>
        Carregando leitores RFID...
      </div>
    );
  }

  // Not `rfidZones.length === 0`: a person stripped of every zone can still have credentials
  // sitting on readers, and that is exactly when this section must NOT disappear.
  if (rfidTableRows.length === 0) return null;


  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        {/* Plain heading, matching "Zonas de Acesso" directly above it — the icon and the count
            pill made two sibling sections of the same page look like different features. The
            table underneath already shows how many there are. */}
        {/* No action here: this section REPORTS what reached each reader. Its old
            "Sincronizar RFID" button synced a strict subset of what the Sincronizar above
            already covers (everything in every zone), so pressing one or the other decided,
            invisibly, how much got synced. The zone table owns the actions now. */}
        <h4 className={cn('text-sm font-medium', darkMode ? 'text-gray-300' : 'text-gray-700')}>
          Dispositivos de Acesso
        </h4>
      </div>

      {orphanCount > 0 && (
        <div
          data-testid="orphan-credentials-warning"
          className={cn(
            'mb-3 rounded-lg border px-3 py-2 text-xs',
            darkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700',
          )}
        >
          <strong>{orphanCount}</strong>{' '}
          {orphanCount === 1 ? 'dispositivo ainda possui' : 'dispositivos ainda possuem'} a credencial
          desta pessoa sem estar em nenhuma zona de acesso atual. A pessoa continua conseguindo
          passar nesses leitores — use <strong>Revogar</strong> (no topo do cadastro) para limpar.
        </div>
      )}

      {/* One table, not a card per zone. The zone is a column now: an operator reading this is
          answering "where can this person get in, and is the credential really there", and the
          per-row sync/revoke icons turned that reference into a control panel — with a
          destructive action one mis-click from a scroll. Bulk sync stays in the header. */}
      <DeviceAccessTable
        darkMode={darkMode}
        columns={{ primary: 'Dispositivo', state: 'Estado no dispositivo' }}
        emptyLabel="Nenhum leitor RFID/TAG com acesso"
        rows={tableRows}
      />
    </div>
  );
};
